import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Terminal } from 'lucide-react';
import { useLogin } from '@/hooks/auth/useLogin';
import { Input } from '@/components/common/ui/Input';
import { Button } from '@/components/common/ui/Button.tsx';

interface LoginFormData {
    email: string;
    password: string;
    remember: boolean;
}

const Login: React.FC = () => {
    const [formData, setFormData] = useState<LoginFormData>({
        email: '',
        password: '',
        remember: false
    });
    const [errors, setErrors] = useState<Partial<LoginFormData>>({});
    const { login, isLoading } = useLogin();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: fieldValue }));

        if (errors[name as keyof LoginFormData]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<LoginFormData> = {};

        if (!formData.email) {
            newErrors.email = 'Email sequence required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Malformed email string';
        }

        if (!formData.password) {
            newErrors.password = 'Credential token required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        await login({
            identifier: formData.email,
            password: formData.password
        });
    };

    return (
        /* FIX: Replaced fixed dark colors with adaptive theme boundaries to respect the brightness toggle state */
        <div className="space-y-6 font-mono max-w-md mx-auto p-6 bg-white dark:bg-[#050505] border border-border-light dark:border-[#1A1D24] rounded-2xl shadow-lg dark:shadow-[0_0_50px_rgba(0,0,0,0.8)] relative overflow-hidden transition-all duration-300">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent shadow-[0_0_10px_#02DFC2]"></div>
            
            {/* Header / Identity Logo */}
            <div className="text-center space-y-3">
                <div className="flex justify-center mb-2">
                    <Link to="/" className="flex items-center z-50">
                        <img 
                            src="/image/logo.png" 
                            alt="ThreatInformant" 
                            className="h-20 w-52 object-contain"
                        />
                    </Link>
                </div>
                {/* FIX: Let text colors adapt perfectly between system states */}
                <h1 className="text-md font-bold text-text-primary-light dark:text-white tracking-[0.2em] uppercase flex items-center justify-center gap-2">
                    <Terminal className="h-4 w-4 text-[#02DFC2]" /> Welcome Back
                </h1>
                <p className="text-[10px] text-text-secondary-light dark:text-slate-500 uppercase tracking-widest">
                    Sign in with your Threat Informant account
                </p>
            </div>

            {/* Form Matrix */}
            <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                    label="EMAIL"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email..."
                    className="bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-text-primary-light dark:text-[#02DFC2] h-11 text-[12px] placeholder-text-muted-light dark:placeholder:text-slate-800 focus:border-[#02DFC2]"
                    leftIcon={<Mail className="h-4 w-4 text-text-muted-light dark:text-slate-600" />}
                    error={errors.email}
                    autoComplete="email"
                    disabled={isLoading}
                />

                <Input
                    label="PASSWORD"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password..."
                    className="bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-text-primary-light dark:text-[#02DFC2] h-11 text-[13px] placeholder-text-muted-light dark:placeholder:text-slate-800 focus:border-[#02DFC2]"
                    leftIcon={<Lock className="h-4 w-4 text-text-muted-light dark:text-slate-600" />}
                    error={errors.password}
                    autoComplete="current-password"
                    disabled={isLoading}
                    showPasswordToggle={true}
                />

                {/* State Options */}
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wider pt-1">
                    <div className="flex items-center group cursor-pointer">
                        <input
                            id="remember"
                            name="remember"
                            type="checkbox"
                            checked={formData.remember}
                            onChange={handleChange}
                            className="h-3.5 w-3.5 bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-[#02DFC2] focus:ring-0 rounded cursor-pointer checked:bg-[#02DFC2]"
                            disabled={isLoading}
                        />
                        <label htmlFor="remember" className="ml-2 text-text-secondary-light dark:text-slate-400 group-hover:text-text-primary-light dark:group-hover:text-white transition-colors cursor-pointer select-none">
                            Remember me
                        </label>
                    </div>
                    <Link
                        to="/auth/forgot-password"
                        className="text-text-secondary-light dark:text-slate-500 hover:text-[#FF3333] dark:hover:text-[#FF3333] transition-colors"
                    >
                        Forgot Password ?
                    </Link>
                </div>

                {/* Execution Button */}
                <Button
                    type="submit"
                    className="w-full bg-[#02DFC2] text-black font-black tracking-[0.2em] h-11 shadow-[0_0_15px_rgba(2,223,194,0.2)] hover:bg-[#00c9ae]"
                    size="lg"
                    loading={isLoading}
                >
                    Sign In
                </Button>
            </form>
        </div>
    );
};

export default Login;